import { useEffect } from 'react';
import { LegalLayout } from './LegalLayout';

const EFFECTIVE_DATE = 'May 13, 2026';
const CONTACT_EMAIL = 'hello@sendfast.ai';

/**
 * Privacy Policy — placeholder copy that accurately describes the current
 * data practices (better-auth on D1 + R2 + OpenRouter for AI generation).
 *
 * This is intentionally written in plain English. When we hire counsel or add
 * paid plans, swap this for a vetted version. Until then, this is enough to
 * satisfy Google Safe Browsing / OAuth Consent Screen requirements and to
 * tell users in good faith what we do with their data.
 */
export function PrivacyPage() {
  useEffect(() => {
    document.title = 'Privacy Policy — SendFast';
  }, []);

  return (
    <LegalLayout title="Privacy Policy" effectiveDate={EFFECTIVE_DATE}>
      <p>
        This Privacy Policy explains how SendFast (&ldquo;we&rdquo;, &ldquo;us&rdquo;) collects, uses,
        and shares information when you use sendfast.ai or any of our APIs (the &ldquo;Service&rdquo;).
        We try to keep this short and human-readable. If anything is unclear, email us at{' '}
        <a href={`mailto:${CONTACT_EMAIL}`}>{CONTACT_EMAIL}</a>.
      </p>

      <h2>1. What we collect</h2>
      <h3>Account information</h3>
      <ul>
        <li>
          <strong>Email address</strong> — used to authenticate you, send transactional email
          (verification, receipts), and contact you about your account.
        </li>
        <li>
          <strong>Display name and avatar</strong> — only if you sign in with Google OAuth, in
          which case we receive your basic profile and the email associated with it.
        </li>
      </ul>

      <h3>Content you create</h3>
      <ul>
        <li>
          Email drafts, templates, recipients, and send history that you produce while using the
          Service. Drafts are stored on Cloudflare D1 (SQLite) and any uploaded images on
          Cloudflare R2 (object storage), all within Cloudflare&apos;s infrastructure.
        </li>
        <li>
          Prompts you send to our AI generation features. Prompts are forwarded to our model
          provider (OpenRouter) to produce a response and are not used to train our models.
        </li>
      </ul>

      <h3>Usage and technical data</h3>
      <ul>
        <li>
          IP address, user-agent, request timestamps, and request paths — captured by Cloudflare
          for abuse prevention and operational logging.
        </li>
        <li>
          Aggregate metrics about your sends (counts, success/failure) so we can show you usage
          inside the dashboard and enforce plan limits.
        </li>
      </ul>

      <h2>2. How we use it</h2>
      <ul>
        <li>To provide, maintain, and improve the Service.</li>
        <li>To authenticate you and keep your account secure.</li>
        <li>To send transactional email (verification, receipts, security alerts).</li>
        <li>To prevent abuse, fraud, and illegal use of the Service.</li>
        <li>To comply with applicable law.</li>
      </ul>
      <p>
        We do <strong>not</strong> sell your personal information. We do not use the contents of
        your drafts, recipients, or AI prompts to train any model.
      </p>

      <h2>3. Sub-processors</h2>
      <p>We rely on a small set of vendors to operate the Service:</p>
      <ul>
        <li>
          <strong>Cloudflare</strong> — hosting (Workers), database (D1), object storage (R2),
          DNS, CDN, and DDoS protection.
        </li>
        <li>
          <strong>Resend</strong> — delivery of transactional and marketing email on your behalf.
        </li>
        <li>
          <strong>OpenRouter</strong> — AI model gateway used for email generation features.
        </li>
        <li>
          <strong>Google</strong> — only if you choose to sign in with Google OAuth.
        </li>
      </ul>

      <h2>4. Data retention</h2>
      <p>
        We keep account data and content for as long as your account is active. If you delete
        your account, we delete your drafts, templates, and uploaded assets within 30 days,
        except where we are required to retain records to comply with law (for example, billing
        records).
      </p>

      <h2>5. Your rights</h2>
      <p>
        Depending on where you live, you may have the right to access, correct, export, or
        delete your personal information. To exercise any of these, email{' '}
        <a href={`mailto:${CONTACT_EMAIL}`}>{CONTACT_EMAIL}</a> from the address on your account
        and we&apos;ll respond within a reasonable time.
      </p>

      <h2>6. Cookies and similar technologies</h2>
      <p>
        We use a small number of strictly-necessary cookies to keep you signed in and to protect
        the Service from abuse. We do not use advertising or cross-site tracking cookies.
      </p>

      <h2>7. Children</h2>
      <p>
        SendFast is not intended for children under 16. If you believe a child has signed up,
        contact us and we will remove the account.
      </p>

      <h2>8. International transfers</h2>
      <p>
        Cloudflare may process your data in any region in which it operates infrastructure.
        Where required, we rely on Standard Contractual Clauses or equivalent safeguards.
      </p>

      <h2>9. Changes</h2>
      <p>
        We may update this policy from time to time. If we make material changes, we will notify
        you by email or via an in-app notice before they take effect.
      </p>

      <h2>10. Contact</h2>
      <p>
        Questions, complaints, or data requests:{' '}
        <a href={`mailto:${CONTACT_EMAIL}`}>{CONTACT_EMAIL}</a>.
      </p>
    </LegalLayout>
  );
}
